import { Box, FormLabel, Grid, GridItem } from '@chakra-ui/react'
import { ReactElement, useEffect } from 'react'
import { PermissionsEnum } from '../../../../../utils'
import { CmFieldTypeEnum } from '../../../../constants'
import type { CmFieldResponseObject } from '../../../../interfaces'
import PermissionGuard from '../../../PermissionGuard'
import CMBooleanField from './CMBooleanField'
import CMDateField from './CMDateField'
import CMMultiSelectField from './CMMultiSelectField'
import CMNumberField from './CMNumberField'
import CMSingleSelectField from './CMSingleSelectField'
import CMTextareaField from './CMTextareaField'
import CMTextField from './CMTextField'
import { validator } from '../../NewButton/uploadUtils'

const CMFormField = ({
  cmField,
  disabled,
}: {
  cmField: CmFieldResponseObject
  disabled: boolean
}): React.ReactElement => {
  switch (cmField.schema.type) {
    case CmFieldTypeEnum.Boolean:
      return (
        <CMBooleanField cmField={cmField} disabled={disabled} isUpload={true} />
      )
    case CmFieldTypeEnum.Number:
      return (
        <CMNumberField cmField={cmField} disabled={disabled} isUpload={true} />
      )
    case CmFieldTypeEnum.Text:
      return <CMTextField cmField={cmField} disabled={disabled} validator={validator} isUpload={true} />
    case CmFieldTypeEnum.Textarea:
      return (
        <CMTextareaField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
          validator={validator}
        />
      )
    case CmFieldTypeEnum.SingleSelect:
      return (
        <CMSingleSelectField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
        />
      )
    case CmFieldTypeEnum.MultiSelect:
      return (
        <CMMultiSelectField
          cmField={cmField}
          disabled={disabled}
          isUpload={true}
        />
      )
    case CmFieldTypeEnum.Date:
      return (
        <CMDateField cmField={cmField} disabled={disabled} isUpload={true} />
      )
    default:
      throw Error('Should not happen')
  }
}

const CMUploadForm = ({
  cmFields,
  renderFromHeader = true,
  disableCMField=false
}: {
  cmFields: CmFieldResponseObject[]
  renderFromHeader?: boolean
  disableCMField?:boolean
}): ReactElement => {
  useEffect(() => {
    document.getElementById("cm-upload-form-id")?.scrollIntoView()
  }, [])
  return (
    <Box
      position="relative"
      id="cm-upload-form-id"
      width="100%"
      spacing="1.5em"
      alignItems="flex-start"
      paddingBottom="2em"
    >
      {cmFields.length ? (
        <PermissionGuard
          permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
        >
          {(hasMlManagePermission: boolean) => (
            <>
              {' '}
              {renderFromHeader ? (
                <FormLabel htmlFor="name" fontWeight={700}>
                  Custom metadata
                </FormLabel>
              ) : null}
              <Box
                display="flex"
                flexWrap="wrap"
                gap="20px"
                marginTop={renderFromHeader ? '20px' : 0}
              >
                <Grid templateColumns="repeat(11, 1fr)" gap={4} width="100%">
                  {cmFields.map((cmField: CmFieldResponseObject, index) =>
                    index % 2 == 0 ? (
                      <GridItem colSpan={5} h="fit-content" key={cmField.id}>
                        <CMFormField
                          cmField={cmField}
                          disabled={!hasMlManagePermission || disableCMField}
                        />
                      </GridItem>
                    ) : (
                      <GridItem
                        colStart={7}
                        colEnd={12}
                        h="fit-content"
                        key={cmField.id}
                      >
                        <CMFormField
                          cmField={cmField}
                          disabled={!hasMlManagePermission || disableCMField}
                        />
                      </GridItem>
                    ),
                  )}
                </Grid>
              </Box>
            </>
          )}
        </PermissionGuard>
      ) : (
        <></>
      )}
    </Box>
  )
}

export default CMUploadForm

export interface CMFieldProps {
  cmField: CmFieldResponseObject
  disabled?: boolean
}
